import React, { useState, useEffect } from 'react';
import './WorkSchedule.css';
import { api, useLocalStorageFallback } from '../services/api';

function WorkSchedule({ user, onBack }) {
  const [currentDate, setCurrentDate] = useState(new Date());
  const [selectedUser, setSelectedUser] = useState(user);
  const [users, setUsers] = useState([]);
  const [scheduleData, setScheduleData] = useState({});
  const [unavailability, setUnavailability] = useState({});
  const [isLoading, setIsLoading] = useState(true);
  const [showConflictModal, setShowConflictModal] = useState(false);
  const [selectedConflict, setSelectedConflict] = useState(null);
  
  // Drag selection state
  const [isDragging, setIsDragging] = useState(false);
  const [dragStart, setDragStart] = useState(null);
  const [dragEnd, setDragEnd] = useState(null);
  const [selectedSlots, setSelectedSlots] = useState([]);
  
  // Use refs for immediate drag state updates
  const isDraggingRef = React.useRef(false);
  const dragStartRef = React.useRef(null);
  const dragEndRef = React.useRef(null);
  
  // Manager mode state
  const [managerMode, setManagerMode] = useState('availability'); // 'availability' or 'assign_work'
  const [viewMode, setViewMode] = useState('week'); // 'week' or 'month' or 'overview'
  const [officeHours, setOfficeHours] = useState({
    sunday: { start: '08:00', end: '18:00', enabled: true },
    monday: { start: '08:00', end: '18:00', enabled: true },
    tuesday: { start: '08:00', end: '18:00', enabled: true },
    wednesday: { start: '08:00', end: '18:00', enabled: true },
    thursday: { start: '08:00', end: '18:00', enabled: true },
    friday: { start: '08:00', end: '18:00', enabled: true },
    saturday: { start: '08:00', end: '18:00', enabled: false }
  });
  const [showOfficeHoursModal, setShowOfficeHoursModal] = useState(false);
  const [allUsersSchedules, setAllUsersSchedules] = useState({});
  const [allUsersUnavailability, setAllUsersUnavailability] = useState({});
  
  const useFallback = useLocalStorageFallback();
  const isManager = user.role === 'Manager' || user.isAdmin === true;
  
  console.log('WorkSchedule - User object:', user);
  console.log('WorkSchedule - isManager:', isManager);
  console.log('WorkSchedule - managerMode:', managerMode);

  // Load users (for managers)
  useEffect(() => {
    const loadUsers = async () => {
      if (isManager) {
        if (useFallback) {
          const savedUsers = localStorage.getItem('zenio_users');
          if (savedUsers) {
            setUsers(JSON.parse(savedUsers));
          }
        } else {
          try {
            const result = await api.getUsers();
            if (result.success && result.users) {
              setUsers(result.users);
            }
          } catch (error) {
            console.error('Failed to load users:', error);
          }
        }
      }
      setIsLoading(false);
    };
    loadUsers();
  }, [isManager, useFallback]);

  // Load office hours (for managers/admins)
  useEffect(() => {
    const loadOfficeHours = async () => {
      if (isManager) {
        const storageKey = 'zenio_office_hours';
        if (useFallback) {
          const saved = localStorage.getItem(storageKey);
          if (saved) {
            setOfficeHours(JSON.parse(saved));
          }
        } else {
          // TODO: Load from API when implemented
          const saved = localStorage.getItem(storageKey);
          if (saved) {
            setOfficeHours(JSON.parse(saved));
          }
        }
      }
    };
    loadOfficeHours();
  }, [isManager, useFallback]);

  // Load all users' schedules for overview mode
  useEffect(() => {
    const loadAllSchedules = async () => {
      if (isManager && viewMode === 'overview' && users.length > 0) {
        if (useFallback) {
          const savedSchedule = localStorage.getItem('zenio_work_schedule');
          const savedUnavailability = localStorage.getItem('zenio_unavailability');
          
          if (savedSchedule) {
            setAllUsersSchedules(JSON.parse(savedSchedule));
          }
          
          if (savedUnavailability) {
            setAllUsersUnavailability(JSON.parse(savedUnavailability));
          }
        } else {
          try {
            // Use new batch endpoints for efficiency
            const [schedulesResult, unavailabilityResult] = await Promise.all([
              api.getAllWorkSchedules(),
              api.getAllUnavailability()
            ]);
            
            if (schedulesResult.success) {
              setAllUsersSchedules(schedulesResult.schedules || {});
            }
            if (unavailabilityResult.success) {
              setAllUsersUnavailability(unavailabilityResult.unavailability || {});
            }
          } catch (error) {
            console.error('Failed to load all schedules:', error);
          }
        }
      }
    };
    
    loadAllSchedules();
  }, [isManager, viewMode, users, useFallback]);

  // Load schedule and availability data
  useEffect(() => {
    const loadScheduleData = async () => {
      const userId = selectedUser.id || selectedUser.userId;
      
      if (useFallback) {
        const savedSchedule = localStorage.getItem('zenio_work_schedule');
        const savedUnavailability = localStorage.getItem('zenio_unavailability');
        
        if (savedSchedule) {
          const allSchedules = JSON.parse(savedSchedule);
          setScheduleData(allSchedules[userId] || {});
        }
        
        if (savedUnavailability) {
          const allUnavailability = JSON.parse(savedUnavailability);
          setUnavailability(allUnavailability[userId] || {});
        }
      } else {
        try {
          const scheduleResult = await api.getWorkSchedule(userId);
          const unavailabilityResult = await api.getUnavailability(userId);
          
          if (scheduleResult.success) {
            setScheduleData(scheduleResult.schedule || {});
          }
          if (unavailabilityResult.success) {
            setUnavailability(unavailabilityResult.unavailability || {});
          }
        } catch (error) {
          console.error('Failed to load schedule data:', error);
        }
      }
    };
    
    if (selectedUser) {
      loadScheduleData();
    }
  }, [selectedUser, useFallback]);

  // Get week days (starting on Sunday)
  const getWeekDays = () => {
    const start = new Date(currentDate);
    const day = start.getDay();
    const diff = start.getDate() - day; // Sunday is 0, so subtract day to get to Sunday
    start.setDate(diff);
    
    const days = [];
    for (let i = 0; i < 7; i++) {
      const date = new Date(start);
      date.setDate(start.getDate() + i);
      days.push(date);
    }
    return days;
  };

  // Get month days
  const getMonthDays = () => {
    const year = currentDate.getFullYear();
    const month = currentDate.getMonth();
    
    const firstDay = new Date(year, month, 1);
    const lastDay = new Date(year, month + 1, 0);
    
    // Get the first Sunday before or on the first day of the month
    const firstDayOfWeek = firstDay.getDay();
    const startDate = new Date(firstDay);
    startDate.setDate(startDate.getDate() - firstDayOfWeek);
    
    const days = [];
    let currentDay = new Date(startDate);
    
    // Fill up to 6 weeks (42 days) to ensure consistent calendar size
    for (let i = 0; i < 42; i++) {
      days.push(new Date(currentDay));
      currentDay.setDate(currentDay.getDate() + 1);
    }
    
    return days;
  };

  // Get day name from date
  const getDayName = (date) => {
    const days = ['sunday', 'monday', 'tuesday', 'wednesday', 'thursday', 'friday', 'saturday'];
    return days[date.getDay()];
  };

  // Generate time slots (24 hours or filtered by office hours)
  const generateTimeSlots = (date = null) => {
    let startHour = 0;
    let endHour = 24;
    
    // If date is provided and office hours are enabled for that day
    if (date && isManager) {
      const dayName = getDayName(date);
      const dayHours = officeHours[dayName];
      
      if (dayHours && dayHours.enabled) {
        startHour = parseInt(dayHours.start.split(':')[0]);
        endHour = parseInt(dayHours.end.split(':')[0]) + 1; // +1 to include the end hour
      }
    }
    
    const slots = [];
    for (let hour = startHour; hour < endHour; hour++) {
      slots.push(`${hour.toString().padStart(2, '0')}:00`);
    }
    return slots;
  };

  // Format date key for storage
  const formatDateKey = (date) => {
    return `${date.getFullYear()}-${(date.getMonth() + 1).toString().padStart(2, '0')}-${date.getDate().toString().padStart(2, '0')}`;
  };

  // Get slot key
  const getSlotKey = (date, time) => {
    const dateKey = formatDateKey(date);
    return `${dateKey}_${time}`;
  };

  // Check if user is unavailable at a specific time
  const isUnavailable = (date, time) => {
    const slotKey = getSlotKey(date, time);
    return unavailability[slotKey] === true;
  };

  // Check if work is scheduled at a specific time
  const isScheduled = (date, time) => {
    const slotKey = getSlotKey(date, time);
    return scheduleData[slotKey] !== undefined;
  };

  // Get schedule info for a slot
  const getScheduleInfo = (date, time) => {
    const slotKey = getSlotKey(date, time);
    return scheduleData[slotKey];
  };

  // Get schedule status
  const getScheduleStatus = (date, time) => {
    const info = getScheduleInfo(date, time);
    return info?.status || 'accepted'; // Default to accepted for backward compatibility
  };

  // Check if slot is in drag selection
  const isSlotSelected = (dayIndex, timeIndex) => {
    if (!isDragging || !dragStart || !dragEnd) return false;
    
    const minDay = Math.min(dragStart.day, dragEnd.day);
    const maxDay = Math.max(dragStart.day, dragEnd.day);
    const minTime = Math.min(dragStart.time, dragEnd.time);
    const maxTime = Math.max(dragStart.time, dragEnd.time);
    
    return dayIndex >= minDay && dayIndex <= maxDay && 
           timeIndex >= minTime && timeIndex <= maxTime;
  };

  // Handle mouse down (start drag)
  const handleMouseDown = (dayIndex, timeIndex, date, time, event) => {
    event.preventDefault(); // Prevent text selection
    
    console.log('Mouse down:', dayIndex, timeIndex, date, time);
    
    const dragData = { 
      day: dayIndex, 
      time: timeIndex,  // Store the INDEX, not the time string
      date, 
      timeString: time  // Store the actual time string separately
    };
    
    // Update both state and refs immediately
    setIsDragging(true);
    setDragStart(dragData);
    setDragEnd(dragData);
    
    isDraggingRef.current = true;
    dragStartRef.current = dragData;
    dragEndRef.current = dragData;
  };

  // Handle mouse enter (continue drag)
  const handleMouseEnter = (dayIndex, timeIndex, date, time) => {
    if (isDraggingRef.current) {
      const dragData = { 
        day: dayIndex, 
        time: timeIndex,  // Store the INDEX
        date, 
        timeString: time 
      };
      setDragEnd(dragData);
      dragEndRef.current = dragData;
    }
  };

  // Handle mouse up (end drag and apply changes)
  const handleMouseUp = async (event) => {
    console.log('Mouse up triggered');
    console.log('isDraggingRef:', isDraggingRef.current);
    console.log('dragStartRef:', dragStartRef.current);
    console.log('dragEndRef:', dragEndRef.current);
    
    if (!isDraggingRef.current || !dragStartRef.current || !dragEndRef.current) {
      console.log('Exiting early - drag state not set');
      setIsDragging(false);
      isDraggingRef.current = false;
      return;
    }

    const weekDays = getWeekDays();
    const timeSlots = generateTimeSlots();
    
    const minDay = Math.min(dragStartRef.current.day, dragEndRef.current.day);
    const maxDay = Math.max(dragStartRef.current.day, dragEndRef.current.day);
    const minTime = Math.min(dragStartRef.current.time, dragEndRef.current.time);
    const maxTime = Math.max(dragStartRef.current.time, dragEndRef.current.time);
    
    const slots = [];
    for (let d = minDay; d <= maxDay; d++) {
      for (let t = minTime; t <= maxTime; t++) {
        slots.push({ date: weekDays[d], time: timeSlots[t] });
      }
    }

    console.log('Slots to toggle:', slots);

    const currentUserId = user.id || user.userId;
    const selectedUserId = selectedUser.id || selectedUser.userId;
    const isViewingSelf = currentUserId === selectedUserId;
    
    console.log('Manager mode:', managerMode);
    console.log('Is manager:', isManager);
    console.log('Selected user ID:', selectedUserId);
    console.log('Is viewing self:', isViewingSelf);
    
    if (managerMode === 'assign_work' && isManager) {
      // Manager assigning work
      console.log('Calling toggleWorkSchedule');
      await toggleWorkSchedule(slots, selectedUserId);
    } else {
      // Setting availability - ONLY allowed for yourself
      if (isViewingSelf) {
        console.log('Calling toggleAvailability');
        await toggleAvailability(slots, selectedUserId);
      } else {
        console.log('Cannot set availability for another user');
        alert('You can only set your own availability. Use "Assign Work Mode" to schedule work for others.');
      }
    }

    setIsDragging(false);
    setDragStart(null);
    setDragEnd(null);
    isDraggingRef.current = false;
    dragStartRef.current = null;
    dragEndRef.current = null;
  };

  // Toggle availability for multiple slots
  const toggleAvailability = async (slots, userId) => {
    console.log('toggleAvailability called:', slots, userId);
    
    if (!slots || slots.length === 0) {
      console.log('No slots to toggle');
      return;
    }
    
    const newUnavailability = { ...unavailability };
    
    // Determine if we're marking as available or unavailable based on first slot
    const firstSlotKey = getSlotKey(slots[0].date, slots[0].time);
    const markAsUnavailable = !newUnavailability[firstSlotKey];
    
    // Apply to all slots
    slots.forEach(({ date, time }) => {
      const slotKey = getSlotKey(date, time);
      if (markAsUnavailable) {
        newUnavailability[slotKey] = true;
      } else {
        delete newUnavailability[slotKey];
      }
    });
    
    setUnavailability(newUnavailability);
    
    // Save to storage
    if (useFallback) {
      const allUnavailability = JSON.parse(localStorage.getItem('zenio_unavailability') || '{}');
      allUnavailability[userId] = newUnavailability;
      localStorage.setItem('zenio_unavailability', JSON.stringify(allUnavailability));
    } else {
      try {
        await api.updateUnavailability(userId, newUnavailability);
      } catch (error) {
        console.error('Failed to update unavailability:', error);
      }
    }
  };

  // Toggle work schedule for multiple slots
  const toggleWorkSchedule = async (slots, userId) => {
    console.log('toggleWorkSchedule called:', slots, userId);
    
    if (!slots || slots.length === 0) {
      console.log('No slots to toggle');
      return;
    }
    
    const newScheduleData = { ...scheduleData };
    
    // Determine if we're adding or removing based on first slot
    const firstSlotKey = getSlotKey(slots[0].date, slots[0].time);
    const isRemoving = newScheduleData[firstSlotKey] !== undefined;
    
    let hasPendingConflict = false;
    
    // Apply to all slots
    slots.forEach(({ date, time }) => {
      const slotKey = getSlotKey(date, time);
      
      if (isRemoving) {
        delete newScheduleData[slotKey];
      } else {
        // Check if user is unavailable
        const isSlotUnavailable = isUnavailable(date, time);
        const status = isSlotUnavailable ? 'pending_approval' : 'accepted';
        
        if (isSlotUnavailable) {
          hasPendingConflict = true;
        }
        
        newScheduleData[slotKey] = {
          scheduledBy: user.name,
          scheduledAt: new Date().toISOString(),
          status: status
        };
      }
    });
    
    setScheduleData(newScheduleData);
    
    // Save to storage
    if (useFallback) {
      const allSchedules = JSON.parse(localStorage.getItem('zenio_work_schedule') || '{}');
      allSchedules[userId] = newScheduleData;
      localStorage.setItem('zenio_work_schedule', JSON.stringify(allSchedules));
    } else {
      try {
        await api.updateWorkSchedule(userId, newScheduleData);
      } catch (error) {
        console.error('Failed to update schedule:', error);
      }
    }
    
    if (hasPendingConflict && !isRemoving) {
      alert(`⚠️ Some slots conflict with ${selectedUser.name}'s unavailability. They will need to accept or reject.`);
    }
  };

  // Handle conflict click (user viewing their conflicts)
  const handleConflictClick = (date, time) => {
    const scheduleInfo = getScheduleInfo(date, time);
    const status = getScheduleStatus(date, time);
    
    if (status === 'pending_approval') {
      setSelectedConflict({ date, time, info: scheduleInfo });
      setShowConflictModal(true);
    }
  };

  // Handle accept conflict
  const handleAcceptConflict = async () => {
    if (!selectedConflict) return;
    
    const slotKey = getSlotKey(selectedConflict.date, selectedConflict.time);
    const userId = selectedUser.id || selectedUser.userId;
    
    const newScheduleData = {
      ...scheduleData,
      [slotKey]: {
        ...scheduleData[slotKey],
        status: 'accepted',
        acceptedAt: new Date().toISOString()
      }
    };
    
    setScheduleData(newScheduleData);
    
    // Save to storage
    if (useFallback) {
      const allSchedules = JSON.parse(localStorage.getItem('zenio_work_schedule') || '{}');
      allSchedules[userId] = newScheduleData;
      localStorage.setItem('zenio_work_schedule', JSON.stringify(allSchedules));
    } else {
      try {
        await api.updateWorkSchedule(userId, newScheduleData);
      } catch (error) {
        console.error('Failed to update schedule:', error);
      }
    }
    
    setShowConflictModal(false);
    setSelectedConflict(null);
  };

  // Handle reject conflict
  const handleRejectConflict = async () => {
    if (!selectedConflict) return;
    
    const slotKey = getSlotKey(selectedConflict.date, selectedConflict.time);
    const userId = selectedUser.id || selectedUser.userId;
    
    const newScheduleData = {
      ...scheduleData,
      [slotKey]: {
        ...scheduleData[slotKey],
        status: 'rejected',
        rejectedAt: new Date().toISOString(),
        rejectionReason: 'User not available at this time'
      }
    };
    
    setScheduleData(newScheduleData);
    
    // Save to storage
    if (useFallback) {
      const allSchedules = JSON.parse(localStorage.getItem('zenio_work_schedule') || '{}');
      allSchedules[userId] = newScheduleData;
      localStorage.setItem('zenio_work_schedule', JSON.stringify(allSchedules));
    } else {
      try {
        await api.updateWorkSchedule(userId, newScheduleData);
      } catch (error) {
        console.error('Failed to update schedule:', error);
      }
    }
    
    setShowConflictModal(false);
    setSelectedConflict(null);
  };

  // Navigate weeks
  const navigateWeek = (direction) => {
    const newDate = new Date(currentDate);
    newDate.setDate(newDate.getDate() + (direction * 7));
    setCurrentDate(newDate);
  };

  // Navigate months
  const navigateMonth = (direction) => {
    const newDate = new Date(currentDate);
    newDate.setMonth(newDate.getMonth() + direction);
    setCurrentDate(newDate);
  };

  // Save office hours
  const saveOfficeHours = async () => {
    const storageKey = 'zenio_office_hours';
    
    if (useFallback) {
      localStorage.setItem(storageKey, JSON.stringify(officeHours));
    } else {
      // TODO: Save to API when implemented
      localStorage.setItem(storageKey, JSON.stringify(officeHours));
    }
    
    setShowOfficeHoursModal(false);
    alert('Office hours saved successfully!');
  };

  // Get summary for a day in month view
  const getDaySummary = (date) => {
    const userId = selectedUser.id || selectedUser.userId;
    let scheduledCount = 0;
    let unavailableCount = 0;
    
    const timeSlots = generateTimeSlots(date);
    timeSlots.forEach(time => {
      const slotKey = getSlotKey(date, time);
      if (scheduleData[slotKey]) scheduledCount++;
      if (unavailability[slotKey]) unavailableCount++;
    });
    
    return { scheduledCount, unavailableCount };
  };

  const weekDays = getWeekDays();
  const monthDays = getMonthDays();
  const timeSlots = (viewMode === 'week' || viewMode === 'overview') ? generateTimeSlots(weekDays[0]) : generateTimeSlots();
  const currentUserId = user.id || user.userId;
  const selectedUserId = selectedUser.id || selectedUser.userId;
  const isViewingSelf = currentUserId === selectedUserId;

  return (
    <div className="work-schedule" onMouseUp={handleMouseUp} onMouseLeave={handleMouseUp}>
      <div className="work-schedule-header">
        <button className="back-button" onClick={onBack}>← Back</button>
        <h1>Work Schedule</h1>
        <div className="header-controls">
          {isManager && (
            <>
              {viewMode !== 'overview' && (
                <select 
                  className="user-select"
                  value={selectedUserId}
                  onChange={(e) => {
                    const selectedValue = e.target.value;
                    const foundUser = users.find(u => {
                      const uId = (u.id || u.userId).toString();
                      return uId === selectedValue.toString();
                    });
                    if (foundUser) {
                      setSelectedUser(foundUser);
                    }
                  }}
                >
                  {users.map(u => (
                    <option key={u.id || u.userId} value={u.id || u.userId}>
                      {u.name} {(u.id || u.userId) === currentUserId ? '(You)' : ''}
                    </option>
                  ))}
                </select>
              )}
              
              <button 
                className={`mode-toggle-button ${viewMode === 'week' ? 'active' : ''}`}
                onClick={() => setViewMode('week')}
              >
                📅 Week View
              </button>
              <button 
                className={`mode-toggle-button ${viewMode === 'month' ? 'active' : ''}`}
                onClick={() => setViewMode('month')}
              >
                📆 Month View
              </button>
              <button 
                className={`mode-toggle-button ${viewMode === 'overview' ? 'active' : ''}`}
                onClick={() => setViewMode('overview')}
              >
                👥 Overview
              </button>
              <button 
                className="mode-toggle-button"
                onClick={() => setShowOfficeHoursModal(true)}
                title="Configure office hours"
              >
                🕐 Office Hours
              </button>
              
              {viewMode !== 'overview' && (
                <>
                  <button 
                    className={`mode-toggle-button ${managerMode === 'availability' ? 'active' : ''}`}
                    onClick={() => setManagerMode('availability')}
                  >
                    📋 Availability
                  </button>
                  <button 
                    className={`mode-toggle-button ${managerMode === 'assign_work' ? 'active' : ''}`}
                    onClick={() => setManagerMode('assign_work')}
                  >
                    ⚡ Assign Work
                  </button>
                </>
              )}
            </>
          )}
        </div>
      </div>

      <div className="work-schedule-content">
        <div className="calendar-controls">
          {viewMode === 'week' || viewMode === 'overview' ? (
            <>
              <button onClick={() => navigateWeek(-1)} className="nav-button">← Previous Week</button>
              <h2>
                {weekDays[0].toLocaleDateString('en-US', { month: 'long', day: 'numeric', year: 'numeric' })}
                {' - '}
                {weekDays[6].toLocaleDateString('en-US', { month: 'long', day: 'numeric', year: 'numeric' })}
              </h2>
              <button onClick={() => navigateWeek(1)} className="nav-button">Next Week →</button>
            </>
          ) : (
            <>
              <button onClick={() => navigateMonth(-1)} className="nav-button">← Previous Month</button>
              <h2>
                {currentDate.toLocaleDateString('en-US', { month: 'long', year: 'numeric' })}
              </h2>
              <button onClick={() => navigateMonth(1)} className="nav-button">Next Month →</button>
            </>
          )}
        </div>

        <div className="legend">
          <div className="legend-item">
            <span className="legend-color available"></span>
            <span>Available</span>
          </div>
          <div className="legend-item">
            <span className="legend-color unavailable"></span>
            <span>Unavailable</span>
          </div>
          <div className="legend-item">
            <span className="legend-color scheduled"></span>
            <span>Scheduled (Accepted)</span>
          </div>
          <div className="legend-item">
            <span className="legend-color scheduled-pending"></span>
            <span>Scheduled (Pending)</span>
          </div>
          <div className="legend-item">
            <span className="legend-color scheduled-rejected"></span>
            <span>Scheduled (Rejected)</span>
          </div>
        </div>

        <div className="calendar-wrapper">
          {viewMode === 'month' && (
            <div className="month-view">
              <div className="month-grid">
                <div className="month-header">
                  <div className="month-day-header">Sun</div>
                  <div className="month-day-header">Mon</div>
                  <div className="month-day-header">Tue</div>
                  <div className="month-day-header">Wed</div>
                  <div className="month-day-header">Thu</div>
                  <div className="month-day-header">Fri</div>
                  <div className="month-day-header">Sat</div>
                </div>
                <div className="month-body">
                  {monthDays.map((day, index) => {
                    const isCurrentMonth = day.getMonth() === currentDate.getMonth();
                    const isToday = day.toDateString() === new Date().toDateString();
                    const summary = getDaySummary(day);
                    
                    return (
                      <div 
                        key={index} 
                        className={`month-day ${!isCurrentMonth ? 'other-month' : ''} ${isToday ? 'today' : ''}`}
                      >
                        <div className="month-day-number">{day.getDate()}</div>
                        {isCurrentMonth && (
                          <div className="month-day-summary">
                            {summary.scheduledCount > 0 && (
                              <div className="summary-item scheduled">
                                📅 {summary.scheduledCount}
                              </div>
                            )}
                            {summary.unavailableCount > 0 && (
                              <div className="summary-item unavailable">
                                🚫 {summary.unavailableCount}
                              </div>
                            )}
                          </div>
                        )}
                      </div>
                    );
                  })}
                </div>
              </div>
            </div>
          )}

          {viewMode === 'overview' && (
            <div className="overview-mode">
              <div className="calendar-grid">
                {/* Header row */}
                <div className="calendar-header">
                  <div className="time-column-header">Time</div>
                  {weekDays.map((day, index) => (
                    <div key={index} className="day-header">
                      <div className="day-name">{day.toLocaleDateString('en-US', { weekday: 'short' })}</div>
                      <div className="day-date">{day.getDate()}</div>
                    </div>
                  ))}
                </div>

                {/* Time slots */}
                <div className="calendar-body">
                  {timeSlots.map((time, timeIndex) => (
                    <div key={time} className="time-row">
                      <div className="time-label">{time}</div>
                      {weekDays.map((day, dayIndex) => {
                        // Collect all users scheduled/unavailable at this time
                        const scheduledUsers = [];
                        const unavailableUsers = [];
                        
                        users.forEach(u => {
                          const userId = u.id || u.userId;
                          const userSchedule = allUsersSchedules[userId] || {};
                          const userUnavailability = allUsersUnavailability[userId] || {};
                          const slotKey = getSlotKey(day, time);
                          
                          if (userSchedule[slotKey]) {
                            scheduledUsers.push({ name: u.name, id: userId });
                          }
                          if (userUnavailability[slotKey]) {
                            unavailableUsers.push({ name: u.name, id: userId });
                          }
                        });
                        
                        const hasScheduled = scheduledUsers.length > 0;
                        const hasUnavailable = unavailableUsers.length > 0;
                        const hasMultipleScheduled = scheduledUsers.length > 1;
                        
                        let slotClass = 'time-slot overview-slot';
                        if (hasScheduled && hasUnavailable) {
                          slotClass += ' overview-mixed';
                        } else if (hasScheduled) {
                          slotClass += ' overview-scheduled';
                        } else if (hasUnavailable) {
                          slotClass += ' overview-unavailable';
                        } else {
                          slotClass += ' overview-available';
                        }
                        
                        const totalUsers = new Set([...scheduledUsers.map(u => u.id), ...unavailableUsers.map(u => u.id)]).size;
                        const title = [
                          scheduledUsers.length > 0 ? `Scheduled: ${scheduledUsers.map(u => u.name).join(', ')}` : '',
                          unavailableUsers.length > 0 ? `Unavailable: ${unavailableUsers.map(u => u.name).join(', ')}` : ''
                        ].filter(Boolean).join('\n') || 'No users';
                        
                        return (
                          <div
                            key={`${dayIndex}-${time}`}
                            className={slotClass}
                            title={title}
                          >
                            <div className="overview-slot-content">
                              {hasScheduled && (
                                <div className="user-count scheduled-count">
                                  📅 {scheduledUsers.length}
                                </div>
                              )}
                              {hasUnavailable && (
                                <div className="user-count unavailable-count">
                                  🚫 {unavailableUsers.length}
                                </div>
                              )}
                              {totalUsers > 0 && (
                                <div className="user-names">
                                  {scheduledUsers.slice(0, 2).map((u, idx) => (
                                    <span key={idx} className="user-initials scheduled-initials">
                                      {u.name.split(' ').map(n => n[0]).join('')}
                                    </span>
                                  ))}
                                  {unavailableUsers.slice(0, 2).map((u, idx) => (
                                    <span key={idx} className="user-initials unavailable-initials">
                                      {u.name.split(' ').map(n => n[0]).join('')}
                                    </span>
                                  ))}
                                  {totalUsers > 4 && (
                                    <span className="more-users">+{totalUsers - 4}</span>
                                  )}
                                </div>
                              )}
                            </div>
                          </div>
                        );
                      })}
                    </div>
                  ))}
                </div>
              </div>
            </div>
          )}

          {viewMode === 'week' && (
            <div className="calendar-grid">
              {/* Header row */}
              <div className="calendar-header">
                <div className="time-column-header">Time</div>
                {weekDays.map((day, index) => (
                  <div key={index} className="day-header">
                    <div className="day-name">{day.toLocaleDateString('en-US', { weekday: 'short' })}</div>
                    <div className="day-date">{day.getDate()}</div>
                  </div>
                ))}
              </div>

              {/* Time slots */}
              <div className="calendar-body">
                {timeSlots.map((time, timeIndex) => (
                  <div key={time} className="time-row">
                    <div className="time-label">{time}</div>
                    {weekDays.map((day, dayIndex) => {
                    const isUnavailableSlot = isUnavailable(day, time);
                    const isScheduledSlot = isScheduled(day, time);
                    const scheduleInfo = getScheduleInfo(day, time);
                    const scheduleStatus = getScheduleStatus(day, time);
                    const isSelected = isSlotSelected(dayIndex, timeIndex);
                    
                    let slotClass = 'time-slot';
                    if (isSelected) {
                      slotClass += ' selecting';
                    } else if (isScheduledSlot) {
                      if (scheduleStatus === 'pending_approval') {
                        slotClass += ' scheduled-pending';
                      } else if (scheduleStatus === 'rejected') {
                        slotClass += ' scheduled-rejected';
                      } else {
                        slotClass += ' scheduled';
                      }
                    } else if (isUnavailableSlot) {
                      slotClass += ' unavailable';
                    } else {
                      slotClass += ' available';
                    }

                    let title = '';
                    if (isScheduledSlot) {
                      title = `Scheduled by ${scheduleInfo.scheduledBy} - ${scheduleStatus}`;
                    } else if (isUnavailableSlot) {
                      title = 'Unavailable';
                    } else {
                      title = 'Available';
                    }

                    return (
                      <div
                        key={`${dayIndex}-${time}`}
                        className={slotClass}
                        onMouseDown={(e) => {
                          // Only handle conflict clicks if it's a pending slot and viewing self
                          if (isViewingSelf && scheduleStatus === 'pending_approval') {
                            e.preventDefault();
                            e.stopPropagation();
                            handleConflictClick(day, time);
                          } else {
                            handleMouseDown(dayIndex, timeIndex, day, time, e);
                          }
                        }}
                        onMouseEnter={() => handleMouseEnter(dayIndex, timeIndex, day, time)}
                        onMouseUp={(e) => {
                          // Handle mouseUp on slot itself for better click detection
                          if (!isViewingSelf || scheduleStatus !== 'pending_approval') {
                            handleMouseUp(e);
                          }
                        }}
                        title={title}
                      >
                        {isScheduledSlot && (
                          <div className="slot-content">
                            {scheduleStatus === 'pending_approval' && <span className="slot-icon">⚠️</span>}
                            {scheduleStatus === 'accepted' && <span className="slot-icon">📅</span>}
                            {scheduleStatus === 'rejected' && <span className="slot-icon">❌</span>}
                          </div>
                        )}
                        {isUnavailableSlot && !isScheduledSlot && (
                          <div className="slot-content">
                            <span className="slot-icon">🚫</span>
                          </div>
                        )}
                      </div>
                    );
                  })}
                </div>
              ))}
            </div>
          </div>
          )}
        </div>

        <div className="instructions">
          {isManager && managerMode === 'assign_work' ? (
            <p>
              <strong>Assign Work Mode:</strong> Click and drag to assign work to {selectedUser.name}. 
              Orange slots indicate pending approval needed.
            </p>
          ) : (
            <p>
              <strong>Availability Mode:</strong> Click and drag to toggle availability. 
              {isViewingSelf && ' Click orange ⚠️ slots to accept or reject scheduled work.'}
            </p>
          )}
        </div>
      </div>

      {/* Conflict Resolution Modal */}
      {showConflictModal && selectedConflict && (
        <div className="modal-overlay" onClick={() => setShowConflictModal(false)}>
          <div className="modal-content conflict-modal" onClick={(e) => e.stopPropagation()}>
            <h3>⚠️ Work Scheduled During Unavailable Time</h3>
            <p>
              <strong>Date:</strong> {selectedConflict.date.toLocaleDateString('en-US', { 
                weekday: 'long', 
                year: 'numeric', 
                month: 'long', 
                day: 'numeric' 
              })}
            </p>
            <p><strong>Time:</strong> {selectedConflict.time}</p>
            <p><strong>Scheduled by:</strong> {selectedConflict.info?.scheduledBy}</p>
            <p>This time was scheduled by your manager, but you marked it as unavailable.</p>
            <p className="conflict-question">Can you work at this time?</p>
            <div className="modal-actions">
              <button 
                className="reject-button" 
                onClick={handleRejectConflict}
              >
                ❌ No, I Cannot Work
              </button>
              <button 
                className="accept-button" 
                onClick={handleAcceptConflict}
              >
                ✅ Yes, I Can Work
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Office Hours Modal */}
      {showOfficeHoursModal && (
        <div className="modal-overlay" onClick={() => setShowOfficeHoursModal(false)}>
          <div className="modal-content office-hours-modal" onClick={(e) => e.stopPropagation()}>
            <h3>🕐 Configure Office Hours</h3>
            <p className="modal-description">
              Set the working hours for each day of the week. These will limit the time slots shown in the schedule.
            </p>
            <div className="office-hours-list">
              {['sunday', 'monday', 'tuesday', 'wednesday', 'thursday', 'friday', 'saturday'].map(day => (
                <div key={day} className="office-hours-row">
                  <label className="day-checkbox">
                    <input 
                      type="checkbox"
                      checked={officeHours[day].enabled}
                      onChange={(e) => setOfficeHours({
                        ...officeHours,
                        [day]: { ...officeHours[day], enabled: e.target.checked }
                      })}
                    />
                    <span className="day-name">{day.charAt(0).toUpperCase() + day.slice(1)}</span>
                  </label>
                  {officeHours[day].enabled && (
                    <div className="time-inputs">
                      <input 
                        type="time"
                        value={officeHours[day].start}
                        onChange={(e) => setOfficeHours({
                          ...officeHours,
                          [day]: { ...officeHours[day], start: e.target.value }
                        })}
                      />
                      <span className="time-separator">to</span>
                      <input 
                        type="time"
                        value={officeHours[day].end}
                        onChange={(e) => setOfficeHours({
                          ...officeHours,
                          [day]: { ...officeHours[day], end: e.target.value }
                        })}
                      />
                    </div>
                  )}
                </div>
              ))}
            </div>
            <div className="modal-actions">
              <button className="cancel-button" onClick={() => setShowOfficeHoursModal(false)}>
                Cancel
              </button>
              <button className="save-button" onClick={saveOfficeHours}>
                💾 Save Office Hours
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

export default WorkSchedule;
